import { useTranslation } from 'react-i18next';

interface UndoRedoControlsProps {
  isAdmin: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}

/** Undo / Redo controls — always available while editing the interior.
    Sits on the right, stacked under the grid / god-mode toggles. */
export default function UndoRedoControls({ isAdmin, canUndo, canRedo, onUndo, onRedo }: UndoRedoControlsProps) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        position: 'absolute',
        top: isAdmin ? 166 : 116,
        right: 16,
        zIndex: 1001,
        display: 'flex',
        gap: 8,
      }}
    >
      {([
        { key: 'undo', enabled: canUndo, onClick: onUndo, title: t('Undo') },
        { key: 'redo', enabled: canRedo, onClick: onRedo, title: t('Redo') },
      ] as const).map(({ key, enabled, onClick, title }) => (
        <button
          key={key}
          onClick={enabled ? onClick : undefined}
          disabled={!enabled}
          aria-label={title}
          title={title}
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(219, 219, 219, 0.3)',
            boxShadow: 'inset 4px 3px 5.9px 0px rgba(0, 0, 0, 0.12)',
            cursor: enabled ? 'pointer' : 'default',
            opacity: enabled ? 1 : 0.35,
            pointerEvents: enabled ? 'auto' : 'none',
            transition: 'opacity 0.15s ease',
            padding: 0,
          }}
        >
          {/* Curved arrow glyphs: undo (←) and redo (→) with correctly attached arrowheads */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            {key === 'undo' ? (
              <>
                <path
                  d="M11 7 H7 A5 5 0 1 0 12 17"
                  stroke="#fff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11 7 L8 4 M11 7 L8 10"
                  stroke="#fff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            ) : (
              <>
                <path
                  d="M13 7 H17 A5 5 0 1 1 12 17"
                  stroke="#fff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13 7 L16 4 M13 7 L16 10"
                  stroke="#fff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            )}
          </svg>
        </button>
      ))}
    </div>
  );
}
